import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { salesRadarService } from '../../services/salesRadarService';

const getScoreColor = (score) => {
  if (score >= 80) return { bg: 'var(--green-100)', color: 'var(--green-700)' };
  if (score >= 60) return { bg: 'var(--blue-100)', color: 'var(--blue-700)' };
  if (score >= 40) return { bg: 'var(--yellow-100)', color: 'var(--yellow-700)' };
  return { bg: 'var(--gray-100)', color: 'var(--gray-700)' };
};

const getStatusColor = (status) => {
  const colors = {
    new: { bg: 'var(--blue-100)', color: 'var(--blue-700)' },
    contacted: { bg: 'var(--purple-100)', color: 'var(--purple-700)' },
    qualified: { bg: 'var(--green-100)', color: 'var(--green-700)' },
    unqualified: { bg: 'var(--red-100)', color: 'var(--red-700)' },
    nurturing: { bg: 'var(--yellow-100)', color: 'var(--yellow-700)' },
  };
  return colors[status] || { bg: 'var(--gray-100)', color: 'var(--gray-700)' };
};

// Mock leads data for demo
const MOCK_LEADS = [
  { id: 1, first_name: 'John', last_name: 'Smith', email: 'john.smith@acme.com', company: 'Acme Corp', score: 92, status: 'qualified', last_activity_at: new Date().toISOString(), conversion_probability: 85 },
  { id: 2, first_name: 'Sarah', last_name: 'Johnson', email: 'sarah.j@techstart.io', company: 'TechStart', score: 85, status: 'contacted', last_activity_at: new Date(Date.now() - 86400000).toISOString(), conversion_probability: 72 },
  { id: 3, first_name: 'Michael', last_name: 'Chen', email: 'mchen@enterprise.com', company: 'Enterprise Inc', score: 78, status: 'qualified', last_activity_at: new Date(Date.now() - 172800000).toISOString(), conversion_probability: 68 },
  { id: 4, first_name: 'Emily', last_name: 'Davis', email: 'emily@startup.co', company: 'Startup Co', score: 65, status: 'nurturing', last_activity_at: new Date(Date.now() - 259200000).toISOString(), conversion_probability: 45 },
  { id: 5, first_name: 'David', last_name: 'Wilson', email: 'dwilson@bigcorp.com', company: 'BigCorp', score: 58, status: 'contacted', last_activity_at: new Date(Date.now() - 345600000).toISOString(), conversion_probability: 38 },
  { id: 6, first_name: 'Lisa', last_name: 'Brown', email: 'lisa.b@innovate.io', company: 'Innovate Labs', score: 45, status: 'new', last_activity_at: new Date(Date.now() - 432000000).toISOString(), conversion_probability: 25 },
  { id: 7, first_name: 'James', last_name: 'Miller', email: 'jmiller@growth.com', company: 'Growth Co', score: 38, status: 'unqualified', last_activity_at: new Date(Date.now() - 518400000).toISOString(), conversion_probability: 12 },
  { id: 8, first_name: 'Anna', last_name: 'Taylor', email: 'anna@cloud.io', company: 'CloudTech', score: 88, status: 'qualified', last_activity_at: new Date(Date.now() - 43200000).toISOString(), conversion_probability: 78 },
];

const SCORE_RANGES = {
  hot: { min: 80, label: 'Hot Leads (80+)', color: 'var(--green-600)' },
  warm: { min: 60, max: 79, label: 'Warm Leads (60-79)', color: 'var(--blue-600)' },
  cool: { min: 40, max: 59, label: 'Cool Leads (40-59)', color: 'var(--yellow-600)' },
  cold: { max: 39, label: 'Cold Leads (<40)', color: 'var(--gray-600)' },
};

const LeadList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0 });
  const [filters, setFilters] = useState({
    status: '',
    score_range: searchParams.get('score_range') || '',
    search: '',
  });
  const [sortBy, setSortBy] = useState('score');
  const [sortDir, setSortDir] = useState('desc');

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        sort_by: sortBy,
        sort_dir: sortDir,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
      };
      
      // Convert score_range to min_score/max_score
      if (params.score_range) {
        const range = SCORE_RANGES[params.score_range];
        if (range) {
          if (range.min) params.min_score = range.min;
          if (range.max) params.max_score = range.max;
        }
        delete params.score_range;
      }
      
      const response = await salesRadarService.getLeads(params);
      if (response.data && response.data.length > 0) {
        setLeads(response.data);
        setPagination(prev => ({ ...prev, total: response.pagination?.total || response.data.length }));
      } else {
        // Use filtered mock data
        let mockData = [...MOCK_LEADS];
        
        // Apply score range filter
        if (filters.score_range) {
          const range = SCORE_RANGES[filters.score_range];
          if (range) {
            mockData = mockData.filter(lead => {
              if (range.min && lead.score < range.min) return false;
              if (range.max && lead.score > range.max) return false;
              return true;
            });
          }
        }
        
        // Apply status filter
        if (filters.status) {
          mockData = mockData.filter(lead => lead.status === filters.status);
        }
        
        // Apply search filter
        if (filters.search) {
          const search = filters.search.toLowerCase();
          mockData = mockData.filter(lead => 
            lead.first_name.toLowerCase().includes(search) ||
            lead.last_name.toLowerCase().includes(search) ||
            lead.email.toLowerCase().includes(search) ||
            (lead.company && lead.company.toLowerCase().includes(search))
          );
        }
        
        // Apply sorting
        mockData.sort((a, b) => {
          let aVal = a[sortBy];
          let bVal = b[sortBy];
          if (sortBy === 'name') {
            aVal = `${a.first_name} ${a.last_name}`;
            bVal = `${b.first_name} ${b.last_name}`;
          }
          if (typeof aVal === 'string') {
            return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
          }
          return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        });
        
        setLeads(mockData);
        setPagination(prev => ({ ...prev, total: mockData.length }));
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      // Use mock data on error
      setLeads(MOCK_LEADS);
      setPagination(prev => ({ ...prev, total: MOCK_LEADS.length }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, sortBy, sortDir, filters]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Update URL when score_range changes
  useEffect(() => {
    if (filters.score_range) {
      setSearchParams({ score_range: filters.score_range });
    } else {
      setSearchParams({});
    }
  }, [filters.score_range, setSearchParams]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const handleLeadClick = (leadId) => {
    navigate(`/sales-radar/leads/${leadId}`);
  };

  const handleViewInsights = (e, leadId) => {
    e.stopPropagation();
    navigate(`/sales-radar/insights?lead_id=${leadId}`);
  };

  const getScoreRangeLabel = () => {
    const range = SCORE_RANGES[filters.score_range];
    return range ? range.label : 'All Leads';
  };

  const totalPages = Math.ceil(pagination.total / pagination.per_page);

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav style={{ marginBottom: '1rem' }}>
        <button onClick={() => navigate('/sales-radar')} className="btn btn-link">
          Sales Radar
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <span style={{ color: 'var(--gray-600)' }}>Leads</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              👥 {getScoreRangeLabel()}
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              {pagination.total} leads • Click on a lead to view details or AI insights
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={fetchLeads}>
              🔄 Refresh
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/leads/new')}>
              + Add Lead
            </button>
          </div>
        </div>

        {/* Score Range Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setFilters({ ...filters, score_range: '' })}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: !filters.score_range ? '2px solid var(--primary-600)' : '1px solid var(--gray-300)',
              background: !filters.score_range ? 'var(--red-50)' : 'white',
              color: !filters.score_range ? 'var(--primary-600)' : 'var(--gray-600)',
              fontWeight: !filters.score_range ? '600' : '400',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            All Leads
          </button>
          {Object.entries(SCORE_RANGES).map(([key, range]) => (
            <button
              key={key}
              onClick={() => setFilters({ ...filters, score_range: key })}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: filters.score_range === key ? `2px solid ${range.color}` : '1px solid var(--gray-300)',
                background: filters.score_range === key ? `${range.color}10` : 'white',
                color: filters.score_range === key ? range.color : 'var(--gray-600)',
                fontWeight: filters.score_range === key ? '600' : '400',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              {range.label}
            </button>
          ))}
        </div>

        {/* Filters Row */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search leads..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '8px',
              fontSize: '0.875rem',
            }}
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="form-select"
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '8px',
              fontSize: '0.875rem',
              background: 'white',
              minWidth: '150px'
            }}
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="unqualified">Unqualified</option>
            <option value="nurturing">Nurturing</option>
          </select>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
              <div className="spinner"></div>
            </div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--gray-50)' }}>
                    {[
                      { key: 'name', label: 'Name' },
                      { key: 'email', label: 'Email' },
                      { key: 'company', label: 'Company' },
                      { key: 'score', label: 'Score' },
                      { key: 'conversion_probability', label: 'Conv. Prob.' },
                      { key: 'status', label: 'Status' },
                      { key: 'last_activity_at', label: 'Last Activity' },
                      { key: 'actions', label: 'Actions' },
                    ].map((col) => (
                      <th
                        key={col.key}
                        onClick={() => col.key !== 'actions' && handleSort(col.key)}
                        style={{
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: 'var(--gray-600)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid var(--gray-200)',
                          cursor: col.key !== 'actions' ? 'pointer' : 'default',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {col.label} {sortBy === col.key && (sortDir === 'asc' ? '↑' : '↓')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                        No leads found matching your criteria
                      </td>
                    </tr>
                  ) : (
                    leads.map((lead) => (
                      <tr
                        key={lead.id}
                        onClick={() => handleLeadClick(lead.id)}
                        style={{
                          borderBottom: '1px solid var(--gray-200)',
                          cursor: 'pointer',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--gray-50)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ fontWeight: 500, color: 'var(--gray-900)' }}>
                            {lead.first_name} {lead.last_name}
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                          {lead.email}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                          {lead.company || '-'}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              ...getScoreColor(lead.score || 0),
                            }}
                          >
                            {lead.score || 0}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          {lead.conversion_probability ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{ 
                                width: '60px', 
                                height: '6px', 
                                background: 'var(--gray-200)', 
                                borderRadius: '9999px',
                                overflow: 'hidden'
                              }}>
                                <div style={{ 
                                  height: '100%', 
                                  width: `${lead.conversion_probability}%`, 
                                  background: lead.conversion_probability >= 70 ? 'var(--green-500)' : lead.conversion_probability >= 40 ? 'var(--yellow-500)' : 'var(--red-500)',
                                  borderRadius: '9999px'
                                }}></div>
                              </div>
                              <span style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                                {lead.conversion_probability}%
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: 'var(--gray-400)', fontSize: '0.875rem' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              textTransform: 'capitalize',
                              ...getStatusColor(lead.status),
                            }}
                          >
                            {lead.status || 'new'}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: 'var(--gray-500)', fontSize: '0.875rem' }}>
                          {lead.last_activity_at
                            ? new Date(lead.last_activity_at).toLocaleDateString()
                            : '-'}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <button
                            onClick={(e) => handleViewInsights(e, lead.id)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: 'var(--purple-100)',
                              color: 'var(--purple-700)',
                              border: '1px solid var(--purple-200)',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                            title="View AI Insights"
                          >
                            🧠 Insights
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1rem',
                    borderTop: '1px solid var(--gray-200)',
                    marginTop: '1rem'
                  }}
                >
                  <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                    Page {pagination.page} of {totalPages}
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                      onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                      disabled={pagination.page <= 1}
                    >
                      Previous
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                      onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                      disabled={pagination.page >= totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadList;
